<template>
	<div class="cassie-vertical-md">
		<FilterBar
			:search-query.sync="privacyPolicySearchQuery"
			search-query-label="Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changePrivacyPolicySearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
		>
			<template #action>
				<PrimaryActionButton
					v-if="userFullPermissions"
					@click="onCreateClick"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Privacy Policies
			</template>
			<template #body>
				<DataTable
					:headers="tableHeaders"
					:items="filteredPrivacyPolicies"
					sort-by="privacyPolicyId"
					sort-desc
					@click:row="onEditPrivacyPolicy"
				>
					<template #item.isGlobalDefault="{ item }">
						<v-icon
							v-if="item.isGlobalDefault"
							small
							class="mr-2"
						>
							mdi-check
						</v-icon>
					</template>
					<template #item.action="{ item }">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									@click="onEditPrivacyPolicy(item)"
								>
									<v-list-item-title>
										{{ userFullPermissions && userHasBrand(item.brandId) ? 'Edit' : 'View' }}
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId)"
									@click="onRemovePrivacyPolicy(item)"
								>
									<v-list-item-title>
										Delete
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<Modal
			v-if="privacyPolicyToBeRemoved"
			width="500px"
		>
			<template #modal-title>
				Delete {{ privacyPolicyToBeRemoved.text }}
			</template>
			<template #modal-content>
				Are you sure you wish to delete this Privacy Policy? <br>
				You will not be able to recover it if you confirm its deletion.
			</template>
			<template #modal-footer>
				<SecondaryActionButton @click="closeDeletePrivacyPolicyModal">
					Close
				</SecondaryActionButton>
				<DeleteButton @click="deletePrivacyPolicy">
					Delete
				</DeleteButton>
			</template>
		</Modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterBar from '../../../../../shared/components/filter-bar.vue'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../shared/components/data-table.vue'
import Modal from '../../../../../shared/components/modal.vue'
import DeleteButton from '../../../../../shared/components/delete-button.vue'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { brandOptions, changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasBrand } from '../../../../../shared/state/brands.js'
import { changePrivacyPolicySearchQuery, privacyPolicySearchQuery } from '../../../../../shared/state/privacy-policies.js'
import { MANAGE_PRIVACY_POLICY } from '../../../router/route-names.js'
import { deletePrivacyPolicy } from '../../../../../shared/utils/api/privacy-policies.js'
import { useLabels } from '../../../../../shared/state/admin-portal-navigation.js'
import { PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS } from '../../../../../shared/permissions/admin-portal-permissions.js'
export default {
	components: {
		FilterBar,
		SecondaryActionButton,
		PrimaryActionButton,
		SectionCard,
		DataTable,
		Modal,
		DeleteButton
	},
	props: {
		privacyPolicies: {
			type: Array,
			default: () => []
		}
	},
	setup () {
		return {
			brandOptions,
			userHasBrand,
			changePrivacyPolicySearchQuery,
			privacyPolicySearchQuery,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId
		}
	},
	data () {
		return {
			tableHeaders: [
				{ text: 'ID', value: 'privacyPolicyId', width: '8%' },
				{ text: useLabels('Brand'), value: 'brandName', width: '12%' },
				{ text: 'Name', value: 'privacyPolicyName', width: '20%' },
				{ text: 'Active Version ID', value: 'activeVersionId', width: '10%' },
				{ text: 'External Version Ref', value: 'externalVersionRef', width: '18%' },
				{ text: 'Global Default?', value: 'isGlobalDefault', width: '10%' },
				{ text: 'Created Date', value: 'createdDate', width: '10%' },
				{ text: 'Action', value: 'action', width: '80px', align: 'right', sortable: false }
			],
			privacyPolicyToBeRemoved: null
		}
	},
	computed: {
		...mapGetters({
			checkPermission: 'auth/productAreaPermission'
		}),
		filteredPrivacyPolicies () {
			return this.privacyPolicies.filter(({ text, brandId, isInternal }) => {
				let check = true
				if (privacyPolicySearchQuery.value) check = text.toLowerCase().includes(privacyPolicySearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && (selectedAdminPortalBrandId.value === brandId)
				if (isInternal) check = false
				return check
			})
		},
		userFullPermissions () {
			return this.checkPermission(PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS)
		},
		brandLabel () {
			return `${useLabels('Brand')}`
		}
	},
	methods: {
		onCreateClick () {
			this.$router.push({ name: MANAGE_PRIVACY_POLICY })
		},
		onEditPrivacyPolicy (privacyPolicyToEdit) {
			this.$router.push({ name: MANAGE_PRIVACY_POLICY, params: { privacyPolicyToEdit } })
		},
		onRemovePrivacyPolicy (privacyPolicy) {
			this.privacyPolicyToBeRemoved = privacyPolicy
		},
		closeDeletePrivacyPolicyModal () {
			this.privacyPolicyToBeRemoved = null
		},
		async deletePrivacyPolicy () {
			await deletePrivacyPolicy(this.privacyPolicyToBeRemoved.privacyPolicyId)
			showSnackbar('This privacy policy has been removed')
			this.closeDeletePrivacyPolicyModal()
			this.$emit('load-privacy-policies')
		}
	}
}
</script>
